import { Box, Checkbox, Skeleton, SvgIcon, SxProps, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ElementItemProps } from "../types";
import ElementFrame from "./ElementFrame";
import { useDevice } from "../../../../utils/DeviceProvider";
import { useEditedColor } from "../../../../hooks/useEditedStyles";
import { useIntl } from "react-intl";
import { getLabel } from "../../../../utils/utils";
import { useLocale } from "../../../../locales/LocaleProvider";

import { ReactComponent as IndeterminateCheckboxIcon } from "../../../../icons/checkbox-intermediate.svg";
import { ResetAdornment, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";

const Chkbox = ({ label: labelParam, configKey, elementKey, disabled }: ElementItemProps<{}>) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<boolean | null>(null);
  const [value, setValue] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const { openContextMenu } = useResetContextMenu();

  const label = getLabel(labelParam, locale);

  const setCheckedValue = useCallback(
    (checked: boolean) => {
      if (configIndex === null) {
        return;
      }
      setValue(checked);
      currentValue[configKey].items[configIndex].value = checked;
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key?: string }) => type === "CHKBOX" && (key === elementKey || `${key}` === elementKey)) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, elementKey]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex]?.value ?? null;
    setSaved(svd);
    const current = currentValue[configKey].items[configIndex]?.value;
    setValue(current ?? null);
    setLoaded(true);
  }, [active, currentValue, config, configIndex, configKey]);

  if (!loaded) {
    return (
      <ElementFrame label={label}>
        <Skeleton variant="rectangular" height={40} sx={{ width: "100%", borderRadius: 1 }} />
      </ElementFrame>
    );
  }

  if (configIndex === -1) {
    return (
      <ElementFrame label={label}>
        <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>
      </ElementFrame>
    );
  }

  return (
    <ChkboxElement
      label={label}
      value={value}
      onChange={setCheckedValue}
      edited={saved !== value}
      disabled={disabled}
      onContextMenu={saved !== value ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value`) : undefined}
    />
  );
};

export const ChkboxElement = ({
  label,
  value,
  onChange,
  sx,
  inputSx,
  disabled,
  edited,
  onContextMenu,
}: {
  label: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
  sx?: SxProps;
  inputSx?: SxProps;
  disabled?: boolean;
  edited?: boolean;
  onContextMenu?: React.MouseEventHandler<HTMLElement>;
}) => {
  const theme = useTheme();
  const { background: editedBackground } = useEditedColor();

  return (
    <ElementFrame label={label} sx={sx} disabled={disabled}>
      <Box
        sx={{
          alignSelf: "stretch",
          backgroundColor: edited ? editedBackground : theme.palette.grey[200],
          borderRadius: 1,
          cursor: "pointer",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          minWidth: 75,
          ...inputSx,
        }}
        onClick={() => onChange(!value)}
        onContextMenu={onContextMenu}
      >
        <Checkbox
          checked={value ?? false}
          indeterminate={value === null}
          onChange={(e, checked) => onChange(checked)}
          sx={{ margin: 0, padding: 1 }}
          indeterminateIcon={<SvgIcon component={IndeterminateCheckboxIcon} color="inherit" />}
          disabled={disabled}
        />
        {onContextMenu ? <ResetAdornment sx={{ mr: 1 }} /> : null}
      </Box>
    </ElementFrame>
  );
};

export default Chkbox;
